
import { useState, useEffect } from "react";
import { Group } from "@/lib/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import GroupFormContent, { GroupFormData } from "./GroupFormContent";
import { useGroupForm } from "./useGroupForm";
import { ProductQuantity } from "./GroupProductQuantities";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";

interface AddGroupFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (group: Group, quantities: ProductQuantity[]) => void;
  initialGroup?: Group;
}

export const AddGroupForm = ({
  open,
  onOpenChange,
  onSave,
  initialGroup,
}: AddGroupFormProps) => {
  const { products, group, isSubmitting, handleFormDataChange, handleSubmit } = useGroupForm({
    initialGroup,
    onSave,
  });
  const isMobile = useIsMobile();

  const [initialProductQuantities, setInitialProductQuantities] = useState<ProductQuantity[]>([]);

  // Load quantities when editing a group
  useEffect(() => {
    const loadQuantities = async () => {
      if (!initialGroup?.id) {
        setInitialProductQuantities([]);
        return;
      }
      
      try {
        const { data, error } = await supabase
          .from('group_product_quantities')
          .select('product_id, group_id, quantity')
          .eq('group_id', initialGroup.id);
          
        if (error) throw error;
        
        if (data && data.length > 0) {
          const quantities: ProductQuantity[] = data.map(item => ({
            productId: item.product_id,
            quantity: item.quantity
          }));
          
          setInitialProductQuantities(quantities);
        } else {
          // Set default quantities (1 for each product)
          const defaultQuantities = initialGroup.productIds.map(id => ({
            productId: id,
            quantity: 1
          }));
          
          setInitialProductQuantities(defaultQuantities);
        }
      } catch (err) {
        console.error("Error loading group quantities:", err);
        // Set default quantities on error
        const defaultQuantities = initialGroup.productIds.map(id => ({
          productId: id,
          quantity: 1
        }));
        
        setInitialProductQuantities(defaultQuantities);
      }
    };
    
    loadQuantities();
  }, [initialGroup]);

  // Calculate max height based on device type and improve iOS scrolling
  const getScrollAreaStyle = () => {
    return {
      maxHeight: isMobile ? '40vh' : 'calc(70vh - 120px)',
      // The following properties are needed for iOS scrolling to work properly
      overflowY: 'auto' as const,
      WebkitOverflowScrolling: 'touch' as const,
    };
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {initialGroup ? "Edit Group" : "Add New Group"}
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full pr-3" style={getScrollAreaStyle()}>
            <div className="p-1 pb-4">
              <GroupFormContent
                products={products}
                initialData={initialGroup}
                initialProductQuantities={initialProductQuantities}
                onChange={handleFormDataChange}
              />
            </div>
          </ScrollArea>
        </div>
        
        <DialogFooter className="mt-4">
          <Button
            onClick={() => handleSubmit()}
            disabled={isSubmitting || !group.name}
          >
            {isSubmitting
              ? initialGroup
                ? "Saving..."
                : "Adding..."
              : initialGroup
                ? "Save Changes"
                : "Add Group"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
